import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import {
  ArrowLeft, Users, Award, Clock, CheckCircle, MessageSquare,
  Filter, GraduationCap, Lightbulb, RefreshCw, ChevronRight,
  ArrowLeftCircle, Brain, TrendingUp, BookOpen, Star, RotateCcw,
  ShieldAlert, LogOut, Download, FileSpreadsheet, FileText, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { QHubFooter } from "@/components/QHubBranding";
import { MarkdownText } from "@/components/MarkdownText";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const BLOOM_COLORS = {
  "Recordar": "bg-gray-100 text-gray-700",
  "Comprender": "bg-blue-100 text-blue-700",
  "Aplicar": "bg-green-100 text-green-700",
  "Analizar": "bg-yellow-100 text-yellow-700",
  "Evaluar": "bg-orange-100 text-orange-700",
  "Crear": "bg-purple-100 text-purple-700",
};

const BLOOM_LEVELS = ["Recordar", "Comprender", "Aplicar", "Analizar", "Evaluar", "Crear"];

const ExportCSVPanel = ({ students = [] }) => {
  const [gradeFilter, setGradeFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [mode, setMode] = useState("unit");      // "unit" | "content"
  const [catalog, setCatalog] = useState({ subjects: [], units: [], contents: [] });
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");

  const availableGrades = useMemo(() => {
    const set = new Set(students.map((s) => String(s.grade ?? "").trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [students]);

  const availableSections = useMemo(() => {
    const pool = gradeFilter
      ? students.filter((s) => String(s.grade ?? "") === String(gradeFilter))
      : students;
    const set = new Set();
    pool.forEach((s) => {
      const sec = (s.section || "").trim().toUpperCase();
      if (sec) set.add(sec);
    });
    return Array.from(set).sort();
  }, [students, gradeFilter]);

  // Reset selection when grade, mode or subject changes
  useEffect(() => {
    setSelected(new Set());
  }, [gradeFilter, mode, subjectFilter]);

  // Load catalog whenever grade or subject changes
  useEffect(() => {
    if (!gradeFilter) {
      setCatalog({ subjects: [], units: [], contents: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const params = { grade: gradeFilter };
        if (subjectFilter && subjectFilter !== "all") params.subject = subjectFilter;
        const r = await axios.get(`${API}/teacher/curriculum-catalog`, {
          params,
          withCredentials: true,
        });
        if (!cancelled) setCatalog(r.data);
      } catch (e) {
        toast.error("No se pudo cargar el catálogo del grado");
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gradeFilter, subjectFilter]);

  const items = mode === "unit" ? catalog.units : catalog.contents;
  const filteredItems = useMemo(
    () => items.filter((it) => it.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  const toggle = (item) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    if (filteredItems.every((it) => selected.has(it))) {
      setSelected((s) => {
        const next = new Set(s);
        filteredItems.forEach((it) => next.delete(it));
        return next;
      });
    } else {
      setSelected((s) => {
        const next = new Set(s);
        filteredItems.forEach((it) => next.add(it));
        return next;
      });
    }
  };

  const handleDownload = async () => {
    if (!gradeFilter) { toast.error("Selecciona un grado"); return; }
    if (selected.size === 0) { toast.error(`Selecciona al menos un ${mode === "unit" ? "unidad" : "contenido"}`); return; }
    setDownloading(true);
    try {
      // Use '|' as separator (never appears in curriculum content) because
      // contents themselves contain commas.
      const params = {
        grade: gradeFilter,
        section: sectionFilter,
        mode,
        items: Array.from(selected).join("|"),
      };
      if (subjectFilter && subjectFilter !== "all") params.subject = subjectFilter;
      const r = await axios.get(`${API}/teacher/grade-averages.csv`, {
        params,
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const subjSlug = subjectFilter && subjectFilter !== "all" ? subjectFilter.replace(/\W+/g, "") : "all";
      a.download = `promedios_grado${gradeFilter}_${sectionFilter}_${subjSlug}_${mode}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      // With responseType:'blob', error bodies arrive as Blob too. Read it to surface the real message.
      let detail = "";
      try {
        const blob = e?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { detail = JSON.parse(txt)?.detail || txt; } catch { detail = txt; }
        }
      } catch (parseErr) {
        console.warn("CSV error-body parse failed:", parseErr);
      }
      const msg = detail || (e?.response ? "Error al generar el CSV" : "Error de red");
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-[#419cca]/5 to-[#8b5cf6]/5 rounded-2xl p-6 border border-[#419cca]/10">
        <div className="flex items-start gap-3 mb-4">
          <FileSpreadsheet className="w-7 h-7 text-[#419cca]" />
          <div>
            <h3 className="font-bold text-lg text-gray-900">Exportar promedios a CSV</h3>
            <p className="text-sm text-gray-600">Selecciona grado, sección y luego elige el modo (unidades o contenidos). Solo puedes usar uno de los dos modos a la vez.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Grado</label>
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="mt-1 bg-white" data-testid="export-grade-select">
                <SelectValue placeholder="Selecciona grado" />
              </SelectTrigger>
              <SelectContent>
                {availableGrades.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`export-grade-opt-${g}`}>{g}°</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Sección</label>
            <Select value={sectionFilter} onValueChange={setSectionFilter} disabled={availableSections.length === 0}>
              <SelectTrigger className="mt-1 bg-white" data-testid="export-section-select">
                <SelectValue placeholder="Todas las secciones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las secciones</SelectItem>
                {availableSections.map((sec) => (
                  <SelectItem key={sec} value={sec} data-testid={`export-section-opt-${sec}`}>Sección {sec}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Materia</label>
            <Select value={subjectFilter} onValueChange={setSubjectFilter} disabled={!gradeFilter || (catalog.subjects || []).length === 0}>
              <SelectTrigger className="mt-1 bg-white" data-testid="export-subject-select">
                <SelectValue placeholder="Todas las materias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las materias</SelectItem>
                {(catalog.subjects || []).map((s) => (
                  <SelectItem key={s} value={s} data-testid={`export-subject-opt-${s.slice(0,30)}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-medium">Modo</label>
            <div className="mt-1 flex bg-white rounded-md border border-gray-200 p-1 gap-1">
              <button
                type="button"
                onClick={() => setMode("unit")}
                className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition ${mode === "unit" ? "bg-[#419cca] text-white" : "text-gray-600 hover:bg-gray-50"}`}
                data-testid="export-mode-unit"
              >
                <FileText className="w-3.5 h-3.5 inline mr-1" /> Unidades
              </button>
              <button
                type="button"
                onClick={() => setMode("content")}
                className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition ${mode === "content" ? "bg-[#419cca] text-white" : "text-gray-600 hover:bg-gray-50"}`}
                data-testid="export-mode-content"
              >
                <FileText className="w-3.5 h-3.5 inline mr-1" /> Contenidos
              </button>
            </div>
          </div>
        </div>

        {gradeFilter ? (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">
                Selecciona {mode === "unit" ? "unidades" : "contenidos"} ({selected.size} elegido{selected.size !== 1 ? "s" : ""})
              </p>
              <button
                type="button"
                onClick={toggleAllFiltered}
                className="text-xs text-[#419cca] hover:underline"
                data-testid="export-toggle-all"
              >
                {filteredItems.every((it) => selected.has(it)) ? "Quitar todos" : "Seleccionar todos"}
              </button>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Buscar ${mode === "unit" ? "unidad" : "contenido"}…`}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-[#419cca]/30"
              data-testid="export-search"
            />
            <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
              {loadingCatalog ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando…
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">Sin {mode === "unit" ? "unidades" : "contenidos"}</div>
              ) : (
                filteredItems.map((it) => (
                  <label
                    key={it}
                    className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm"
                    data-testid={`export-item-${it.slice(0,30)}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(it)}
                      onChange={() => toggle(it)}
                      className="mt-0.5"
                    />
                    <span className="flex-1">{it}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
            Selecciona un grado para cargar las {mode === "unit" ? "unidades" : "contenidos"}.
          </div>
        )}

        <Button
          onClick={handleDownload}
          disabled={!gradeFilter || selected.size === 0 || downloading}
          className="mt-4 bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white gap-2"
          data-testid="export-download-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Descargar CSV
        </Button>
      </div>
    </div>
  );
};

const GroupSuggestionsPanel = ({ students = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");

  // Derive available grades from the teacher's assigned students
  const availableGrades = useMemo(() => {
    const set = new Set(
      students.map((s) => String(s.grade ?? "").trim()).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [students]);

  // Derive available sections for the current grade filter
  const availableSections = useMemo(() => {
    const pool = gradeFilter === "all"
      ? students
      : students.filter((s) => String(s.grade ?? "") === String(gradeFilter));
    const set = new Set();
    pool.forEach((s) => {
      const sec = (s.section || "").trim().toUpperCase();
      if (sec) set.add(sec);
    });
    return Array.from(set).sort();
  }, [students, gradeFilter]);

  // Reset section filter when grade changes and current section is no longer valid
  useEffect(() => {
    if (sectionFilter !== "all" && sectionFilter !== "none" && !availableSections.includes(sectionFilter)) {
      setSectionFilter("all");
    }
  }, [availableSections, sectionFilter]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const params = {};
      if (gradeFilter && gradeFilter !== "all") params.grade = gradeFilter;
      if (sectionFilter && sectionFilter !== "all") params.section = sectionFilter;
      const res = await axios.get(`${API}/teacher/group-suggestions`, {
        params,
        withCredentials: true,
      });
      setData(res.data);
      setGenerated(true);
    } catch (err) {
      toast.error("Error al generar sugerencias grupales");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10 rounded-2xl p-6 border border-[#419cca]/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shrink-0">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">Sugerencias Pedagógicas Grupales</h3>
            <p className="text-sm text-gray-500 mt-1">
              Análisis del desempeño colectivo de tus estudiantes con recomendaciones de enseñanza diferenciada.
            </p>
            {data && (
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-[#419cca]/20 text-[#419cca] font-medium">
                  {data.total_students} estudiantes
                </span>
                <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-[#419cca]/20 text-[#419cca] font-medium">
                  {data.total_evidences} evidencias analizadas
                </span>
                {data.grade && (
                  <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-[#419cca]/20 text-[#419cca] font-medium">
                    Grado {data.grade}
                  </span>
                )}
                {data.section && (
                  <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-[#419cca]/20 text-[#419cca] font-medium">
                    Sección {data.section}
                  </span>
                )}
              </div>
            )}

            {/* Filters: grade + section */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Filtrar:</span>
              <Select value={gradeFilter} onValueChange={setGradeFilter}>
                <SelectTrigger
                  className="h-8 w-auto min-w-[120px] text-xs bg-white border-[#419cca]/30"
                  data-testid="group-suggestions-grade-select"
                >
                  <SelectValue placeholder="Grado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los grados</SelectItem>
                  {availableGrades.map((g) => (
                    <SelectItem key={g} value={g} data-testid={`group-grade-opt-${g}`}>{g}°</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sectionFilter} onValueChange={setSectionFilter} disabled={availableSections.length === 0}>
                <SelectTrigger
                  className="h-8 w-auto min-w-[110px] text-xs bg-white border-[#419cca]/30"
                  data-testid="group-suggestions-section-select"
                >
                  <SelectValue placeholder="Sección" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las secciones</SelectItem>
                  {availableSections.map((sec) => (
                    <SelectItem key={sec} value={sec} data-testid={`group-section-opt-${sec}`}>Sección {sec}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="generate-group-suggestions-btn"
            className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white hover:opacity-90 gap-2 shrink-0"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 animate-spin" />{generated ? "Actualizando..." : "Generando..."}</>
            ) : (
              <><Brain className="w-4 h-4" />{generated ? "Actualizar" : "Generar Sugerencias"}</>
            )}
          </Button>
        </div>
      </div>

      {/* Suggestions output */}
      {!generated && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
          <Brain className="w-12 h-12 text-gray-200 mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Presiona &ldquo;Generar Sugerencias&rdquo; para analizar el desempeño de tu grupo.</p>
        </div>
      )}

      {loading && !data && (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
          <RefreshCw className="w-8 h-8 text-[#419cca] mx-auto mb-4 animate-spin" />
          <p className="text-gray-500 text-sm">Analizando el desempeño del grupo...</p>
        </div>
      )}

      {data?.suggestions && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-gray-100 p-6"
          data-testid="group-suggestions-output"
        >
          <MarkdownText content={data.suggestions} />
        </motion.div>
      )}
    </div>
  );
};

const PasteEventsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/paste-events`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error("Error al cargar eventos de pegado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-lg">Respuestas con patrón de IA detectado</h3>
            <p className="text-sm text-gray-600 mt-1">
              Estudiantes que pegaron texto largo en el tutor. Útil para conversar pedagógicamente con ellos.
              <span className="block text-xs text-gray-400 mt-1">Esta información es solo orientativa: no es una acusación, sino una alerta para seguimiento docente.</span>
            </p>
            {data && (
              <div className="flex gap-4 mt-3 flex-wrap">
                <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-amber-200 text-amber-700 font-medium" data-testid="paste-total-students">
                  {data.total_students} estudiantes
                </span>
                <span className="text-xs bg-white/70 px-3 py-1 rounded-full border border-amber-200 text-amber-700 font-medium" data-testid="paste-total-events">
                  {data.total_events} eventos
                </span>
              </div>
            )}
          </div>
          <Button
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-paste-events-btn"
            variant="outline"
            className="gap-2 shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
          <RefreshCw className="w-8 h-8 text-amber-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500 text-sm">Cargando eventos...</p>
        </div>
      )}

      {data && data.students.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center" data-testid="paste-empty-state">
          <ShieldAlert className="w-12 h-12 text-gray-200 mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Sin eventos sospechosos. ¡Buen trabajo!</p>
        </div>
      )}

      {data && data.students.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Estudiante</TableHead>
                <TableHead>Grado</TableHead>
                <TableHead>Eventos</TableHead>
                <TableHead>Materias</TableHead>
                <TableHead>Último</TableHead>
                <TableHead className="text-right">Detalle</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.students.map((row) => (
                <React.Fragment key={row.student_profile_id}>
                  <TableRow data-testid={`paste-row-${row.student_profile_id}`}>
                    <TableCell className="font-medium">{row.student_name || "-"}</TableCell>
                    <TableCell><Badge variant="outline">{row.grade || "-"}</Badge></TableCell>
                    <TableCell>
                      <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                        {row.paste_suspicions}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {Object.entries(row.subjects || {}).map(([k, v]) => `${SUBJECT_LABELS[k] || k} (${v})`).join(", ") || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{fmt(row.last_ts)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setExpanded((p) => ({ ...p, [row.student_profile_id]: !p[row.student_profile_id] }))}
                        className="text-amber-700 hover:text-amber-800"
                        data-testid={`paste-toggle-${row.student_profile_id}`}
                      >
                        {expanded[row.student_profile_id] ? "Ocultar" : "Ver eventos"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded[row.student_profile_id] && (row.recent_events || []).map((ev, i) => (
                    <TableRow key={`${row.student_profile_id}-ev-${i}`} className="bg-amber-50/40">
                      <TableCell colSpan={6}>
                        <div className="text-xs text-gray-700 space-y-1">
                          <div className="flex gap-3 flex-wrap">
                            <span className="font-medium">{fmt(ev.ts)}</span>
                            <span className="text-amber-700">{ev.paste_chars} chars pegados</span>
                            {ev.subject && <span className="text-gray-500">{SUBJECT_LABELS[ev.subject] || ev.subject}</span>}
                            {ev.unit && <span className="text-gray-500">· {ev.unit}</span>}
                            {ev.topic && <span className="text-gray-500">· {ev.topic}</span>}
                          </div>
                          {ev.snippet && (
                            <div className="text-gray-600 italic bg-white rounded p-2 border border-amber-100">
                              &ldquo;{(ev.snippet || "").slice(0, 240)}{(ev.snippet || "").length > 240 ? "..." : ""}&rdquo;
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const RetoAvanzoPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const DIM_LABELS = {
    comprension: "Comprensión", inferencia: "Inferencia", analisis: "Análisis",
    resolucion: "Resolución", argumentacion: "Argumentación", transferencia: "Transferencia",
    persistencia: "Persistencia", metacognicion: "Metacognición",
  };
  const LEVEL_COLORS = {
    "Alto": "bg-emerald-100 text-emerald-700 border-emerald-200",
    "Medio": "bg-amber-100 text-amber-700 border-amber-200",
    "En desarrollo": "bg-orange-100 text-orange-700 border-orange-200",
    "Por explorar": "bg-gray-100 text-gray-500 border-gray-200",
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/reto-avanzo/students`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error("Error al cargar Reto Avanzo"); }
    finally { setLoading(false); }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/teacher/reto-avanzo/students.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `reto_avanzo_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Informe descargado");
    } catch (e) {
      let detail = "";
      try {
        const blob = e?.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          try { detail = JSON.parse(txt)?.detail || txt; } catch { detail = txt; }
        }
      } catch (parseErr) {
        console.warn("Report error-body parse failed:", parseErr);
      }
      toast.error(detail || "Error al descargar el informe");
    } finally {
      setDownloading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center">
      <RefreshCw className="w-8 h-8 text-sky-500 mx-auto mb-4 animate-spin" />
      <p className="text-gray-500 text-sm">Cargando estudiantes...</p>
    </div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center" data-testid="ra-empty">
        <Brain className="w-12 h-12 text-gray-200 mx-auto mb-4" />
        <p className="text-gray-400 text-sm">Aún no tienes estudiantes de 10° u 11° asignados con Reto Avanzo activo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ra-panel">
      <div className="bg-gradient-to-br from-sky-50 to-indigo-50 rounded-2xl p-6 border border-sky-200 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shrink-0">
          <Brain className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-lg">Reto Avanzo · Resumen del grupo</h3>
          <p className="text-sm text-gray-600 mt-1">
            {data.completed}/{data.total} estudiantes con diagnóstico completado.
          </p>
          {data.weakest_dimensions?.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              <span className="text-xs font-medium text-orange-700">Brechas más comunes:</span>
              {data.weakest_dimensions.map(d => (
                <Badge key={d} variant="outline" className="text-orange-700 border-orange-200">
                  {DIM_LABELS[d] || d}
                </Badge>
              ))}
            </div>
          )}
          {data.strongest_dimensions?.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <span className="text-xs font-medium text-emerald-700">Fortalezas grupales:</span>
              {data.strongest_dimensions.map(d => (
                <Badge key={d} variant="outline" className="text-emerald-700 border-emerald-200">
                  {DIM_LABELS[d] || d}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="ra-reload">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Actualizar
          </Button>
          <Button
            onClick={downloadCsv}
            disabled={downloading || !data || data.total === 0}
            className="gap-2 bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="ra-download-csv"
          >
            <FileSpreadsheet className={`w-4 h-4 ${downloading ? "animate-pulse" : ""}`} />
            {downloading ? "Descargando..." : "Descargar CSV"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estudiante</TableHead>
              <TableHead>Grado</TableHead>
              <TableHead>Diagnóstico</TableHead>
              <TableHead>Perfil</TableHead>
              <TableHead>Interacciones</TableHead>
              <TableHead>Top dimensiones</TableHead>
              <TableHead>Brechas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.students.map(s => {
              const dims = Object.entries(s.dimension_scores || {}).sort((a, b) => b[1] - a[1]);
              const top = dims.slice(0, 2).map(([d]) => d);
              const weak = dims.slice(-2).map(([d]) => d);
              return (
                <TableRow key={s.student_profile_id} data-testid={`ra-row-${s.student_profile_id}`}>
                  <TableCell className="font-medium">{s.student_name || "-"}</TableCell>
                  <TableCell><Badge variant="outline">{s.grade || "-"}</Badge></TableCell>
                  <TableCell>
                    {s.status === "completed"
                      ? <Badge className="bg-emerald-50 text-emerald-700">Completado</Badge>
                      : s.status === "in_progress"
                        ? <Badge className="bg-amber-50 text-amber-700">En curso</Badge>
                        : <Badge className="bg-gray-100 text-gray-500">Pendiente</Badge>}
                  </TableCell>
                  <TableCell>
                    {s.profile_label
                      ? <Badge className="bg-sky-50 text-sky-700">{s.profile_label}</Badge>
                      : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="text-sm">
                    {s.interactions > 0
                      ? <span className="font-medium text-gray-700">{s.interactions}</span>
                      : <span className="text-gray-400">0</span>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {top.map(d => (
                        <Badge key={d} className={`border ${LEVEL_COLORS[(s.dimension_levels || {})[d]] || ""}`}>
                          {DIM_LABELS[d] || d}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {weak.map(d => (
                        <Badge key={d} variant="outline" className="text-orange-700 border-orange-200">
                          {DIM_LABELS[d] || d}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const SUBJECT_LABELS = {
  "comunicacion-lenguaje": "Comunicacion y Lenguaje",
  "matematica-datos": "Matematica y Datos",
  "ciencia-tecnologia": "Ciencia y Tecnologia",
  "ciudadania-valores": "Ciudadania y Valores",
};

const StudentDetail = ({ student, evidences, onBack, progressData }) => {
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const handleRequestSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(
        `${API}/teacher/student-suggestions/${student.id}`,
        { withCredentials: true }
      );
      setSuggestions(res.data.suggestions);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Error al generar sugerencias");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const bloomSummary = {};
  const subjectsSet = new Set();
  (evidences || []).forEach((ev) => {
    const bl = ev.bloom_level || "Sin nivel";
    bloomSummary[bl] = (bloomSummary[bl] || 0) + 1;
    if (ev.subject) subjectsSet.add(ev.subject);
  });

  const unitProgress = progressData?.units || [];
  const overallAverage = Number.isFinite(progressData?.overall_average_percent)
    ? progressData.overall_average_percent
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      data-testid={`student-detail-${student.id}`}
    >
      {/* Back Button */}
      <Button variant="ghost" onClick={onBack} className="mb-4 gap-2 text-gray-600" data-testid="back-to-list">
        <ArrowLeftCircle className="w-4 h-4" /> Volver a la lista
      </Button>

      {/* Student Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10 flex items-center justify-center">
            <GraduationCap className="w-7 h-7 text-[#419cca]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{student.student_name}</h2>
            <Badge variant="outline">{student.grade ? `Grado ${student.grade}` : "Sin grado"}</Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50/60 rounded-xl p-3 text-center">
            <BookOpen className="w-4 h-4 text-blue-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-900">{(evidences || []).length}</p>
            <p className="text-xs text-gray-500">Evidencias</p>
          </div>
          <div className="bg-green-50/60 rounded-xl p-3 text-center">
            <Star className="w-4 h-4 text-green-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-900">{student.evidence_count || 0}</p>
            <p className="text-xs text-gray-500">Total Registros</p>
          </div>
          <div className="bg-purple-50/60 rounded-xl p-3 text-center">
            <Brain className="w-4 h-4 text-purple-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-900">{Object.keys(bloomSummary).length}</p>
            <p className="text-xs text-gray-500">Niveles Bloom</p>
          </div>
          <div className="bg-amber-50/60 rounded-xl p-3 text-center">
            <TrendingUp className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-900">{subjectsSet.size}</p>
            <p className="text-xs text-gray-500">Materias</p>
          </div>
        </div>
      </div>

      {/* AI Suggestions Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" /> Sugerencias Pedagogicas (IA)
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRequestSuggestions}
            disabled={loadingSuggestions}
            className="gap-1.5"
            data-testid={`request-teacher-suggestions-${student.id}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingSuggestions ? "animate-spin" : ""}`} />
            {suggestions ? "Actualizar" : "Generar Sugerencias"}
          </Button>
        </div>

        {loadingSuggestions && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Analizando evidencias y generando recomendaciones pedagogicas...
          </div>
        )}

        {suggestions && !loadingSuggestions && (
          <div
            className="bg-amber-50/50 rounded-xl p-5"
            data-testid={`teacher-suggestions-content-${student.id}`}
          >
            <MarkdownText content={suggestions} />
          </div>
        )}

        {!suggestions && !loadingSuggestions && (
          <p className="text-sm text-gray-400 italic py-4">
            Presiona &ldquo;Generar Sugerencias&rdquo; para obtener recomendaciones pedagogicas personalizadas para {student.student_name}.
          </p>
        )}
      </div>

      {/* Bloom Summary */}
      {Object.keys(bloomSummary).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" /> Niveles de Bloom
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(bloomSummary).map(([level, count]) => (
              <Badge key={level} className={`text-sm px-3 py-1 ${BLOOM_COLORS[level] || "bg-gray-100 text-gray-700"}`}>
                {level}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Unit Indicator Progress */}
      {unitProgress.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6" data-testid="unit-progress-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" /> Progreso por Unidad y Taxonomía
            </h3>
            {overallAverage !== null && (
              <Badge className="bg-blue-50 text-blue-700" data-testid="unit-progress-overall-average">
                Promedio general: {overallAverage}%
              </Badge>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="unit-progress-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Materia</TableHead>
                  <TableHead>Unidad</TableHead>
                  {BLOOM_LEVELS.map((level) => (
                    <TableHead key={level} className="text-center">{level}</TableHead>
                  ))}
                  <TableHead className="text-center">Promedio</TableHead>
                  <TableHead>Indicadores</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unitProgress.map((unit, idx) => {
                  const subjectLabel = SUBJECT_LABELS[unit.subject] || unit.subject;
                  const rowKey = `${unit.subject}-${unit.unit}-${idx}`;
                  return (
                    <TableRow key={rowKey} data-testid={`unit-progress-row-${idx}`}>
                      <TableCell className="font-medium text-gray-900">{subjectLabel}</TableCell>
                      <TableCell className="text-gray-600">{unit.unit}</TableCell>
                      {BLOOM_LEVELS.map((level) => {
                        const levelData = unit.bloom_levels?.[level] || {};
                        const percentValue = Number.isFinite(levelData.percent) ? levelData.percent : 0;
                        const percentLabel = levelData.total ? `${percentValue}%` : "-";
                        return (
                          <TableCell key={level} className="text-center text-sm" data-testid={`unit-progress-${idx}-${level.toLowerCase()}`}>
                            {percentLabel}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center" data-testid={`unit-progress-average-${idx}`}>
                        {Number.isFinite(unit.unit_average_percent) ? `${unit.unit_average_percent}%` : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2" data-testid={`unit-progress-indicators-${idx}`}>
                          {(unit.indicator_details || []).map((indicator, cIdx) => (
                            <div
                              key={`${indicator.code}-${cIdx}`}
                              className={`relative group px-2 py-1 rounded-lg border text-xs flex items-center gap-1.5 transition-all ${
                                indicator.completed
                                  ? "border-green-300 bg-green-50 text-green-700"
                                  : "border-gray-200 bg-white text-gray-600"
                              }`}
                              data-testid={`unit-indicator-${idx}-${cIdx}`}
                            >
                              {indicator.completed && <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />}
                              <span className={`font-semibold ${indicator.completed ? "text-green-700" : "text-gray-700"}`}>{indicator.code}</span>
                              {!indicator.completed && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await axios.post(`${API}/teacher/assign-reinforcement`, {
                                        student_profile_id: student.id || student.profile_id,
                                        subject: unit.subject,
                                        unit: unit.unit,
                                        indicator_code: indicator.code,
                                        indicator_text: indicator.text || "",
                                      }, { withCredentials: true });
                                      toast.success(`Refuerzo asignado: ${indicator.code}`);
                                    } catch (err) {
                                      const msg = err.response?.data?.detail || "Error al asignar";
                                      if (msg.includes("already")) {
                                        toast.info("Ya tiene refuerzo asignado");
                                      } else {
                                        toast.error(msg);
                                      }
                                    }
                                  }}
                                  className="ml-0.5 p-0.5 rounded hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors"
                                  title="Asignar refuerzo con tutor"
                                  data-testid={`assign-reinforcement-${idx}-${cIdx}`}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                </button>
                              )}
                              <div
                                className="absolute z-20 hidden group-hover:block left-1/2 -translate-x-1/2 mt-2 w-64 rounded-lg bg-gray-900 text-white text-xs p-2 shadow-lg top-full"
                                data-testid={`unit-indicator-tooltip-${idx}-${cIdx}`}
                              >
                                {indicator.text || "Sin descripcion"}
                                {indicator.completed && <span className="block mt-1 text-green-300 font-medium">Trabajado</span>}
                                {!indicator.completed && <span className="block mt-1 text-amber-300 font-medium">No trabajado - Clic para asignar refuerzo</span>}
                              </div>
                            </div>
                          ))}
                          {(unit.indicator_details || []).length === 0 && (
                            <span className="text-xs text-gray-400">Sin indicadores</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Evidence List */}
      {(evidences || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-50">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" /> Evidencias de Aprendizaje
            </h3>
          </div>
          <div className="divide-y divide-gray-50">
            {(evidences || []).slice(0, 10).map((ev, i) => (
              <div key={ev.id || `${ev.student_id || ""}-${ev.created_at || ""}-${i}`} className="p-4 hover:bg-gray-50/50 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">{ev.topic}</span>
                  <div className="flex items-center gap-2">
                    <Badge className={BLOOM_COLORS[ev.bloom_level] || "bg-gray-100"} variant="secondary">
                      {ev.bloom_level}
                    </Badge>
                    <span className="text-xs text-gray-400">
                      {ev.created_at ? new Date(ev.created_at).toLocaleDateString("es-SV", { day: "numeric", month: "short" }) : ""}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-1">{SUBJECT_LABELS[ev.subject] || ev.subject} | Unidad: {ev.unit}</p>
                {ev.tutor_assessment && (
                  <p className="text-sm text-gray-600 mt-1">{ev.tutor_assessment}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const TeacherDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [evidencesByStudent, setEvidencesByStudent] = useState({});
  const [progressByStudent, setProgressByStudent] = useState({});
  const [loading, setLoading] = useState(true);
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isIncidentResponsible, setIsIncidentResponsible] = useState(false);

  // Available sections for the active grade filter (drives the pill buttons).
  const availableSections = useMemo(() => {
    const pool = gradeFilter === "all"
      ? students
      : students.filter((s) => String(s.grade ?? "") === String(gradeFilter));
    const set = new Set();
    pool.forEach((s) => {
      const sec = (s.section || "").trim();
      if (sec) set.add(sec.toUpperCase());
    });
    return Array.from(set).sort();
  }, [students, gradeFilter]);

  // Students after applying both grade (server-side already) and section filter.
  const filteredStudents = useMemo(() => {
    if (sectionFilter === "all") return students;
    if (sectionFilter === "none") return students.filter((s) => !s.section);
    return students.filter(
      (s) => String(s.section || "").toUpperCase() === sectionFilter
    );
  }, [students, sectionFilter]);

  // Reset section filter if it becomes invalid after grade change.
  useEffect(() => {
    if (sectionFilter !== "all" && sectionFilter !== "none" && !availableSections.includes(sectionFilter)) {
      setSectionFilter("all");
    }
  }, [availableSections, sectionFilter]);

  useEffect(() => {
    if (user && !["teacher", "admin", "institutional_admin", "super_admin"].includes(user.role)) { navigate("/dashboard"); return; }
    const fetchData = async () => {
      try {
        const studentsUrl = user?.role === "teacher" ? `${API}/teacher/students-assigned` : `${API}/dashboard/students`;

        const [statsRes, metricsRes, studentsRes, certsRes, reflectionsRes, incidentsMeRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
          user?.role === "teacher" ? axios.get(`${API}/teacher/metrics`, { withCredentials: true }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          axios.get(studentsUrl, { params: gradeFilter !== "all" ? { grade: gradeFilter } : {}, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/certificaciones`, { params: gradeFilter !== "all" ? { grade: gradeFilter } : {}, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/dashboard/reflections`, { params: gradeFilter !== "all" ? { grade: gradeFilter } : {}, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/incidents/me`, { withCredentials: true }).catch(() => ({ data: { is_responsible: false } }))
        ]);

        setStats({ ...statsRes.data, teacherMetrics: metricsRes.data });
        setStudents(studentsRes.data);
        setCertifications(certsRes.data);
        setReflections(reflectionsRes.data);
        setIsIncidentResponsible(Boolean(incidentsMeRes.data?.is_responsible));

        const evidencesMap = {};
        await Promise.all(
          (studentsRes.data || []).map(async (s) => {
            try {
              const params = s.student_profile_id
                ? { student_profile_id: s.student_profile_id }
                : s.id
                ? { student_profile_id: s.id }
                : { user_id: s.user_id };

              const evRes = await axios.get(`${API}/dashboard/evidences`, {
                params,
                withCredentials: true,
              });
              evidencesMap[s.id || s.user_id] = evRes.data || [];
            } catch (e) {
              evidencesMap[s.id || s.user_id] = [];
            }
          })
        );

        setEvidencesByStudent(evidencesMap);

        const progressMap = {};
        await Promise.all(
          (studentsRes.data || []).map(async (s) => {
            const key = s.id || s.user_id;
            const profileId = s.student_profile_id || s.id;
            if (!profileId) {
              progressMap[key] = null;
              return;
            }
            try {
              const progressRes = await axios.get(
                `${API}/teacher/unit-indicator-progress/${profileId}`,
                { withCredentials: true }
              );
              progressMap[key] = progressRes.data;
            } catch (e) {
              progressMap[key] = null;
            }
          })
        );
        setProgressByStudent(progressMap);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate, gradeFilter]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.warn("Logout request failed (continuing anyway):", e);
    }
    window.location.href = "/";
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("es-SV", { day: "numeric", month: "short", year: "numeric" });


  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50"><div className="w-10 h-10 border-4 border-[#419cca] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="teacher-dashboard">

      <header className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/")} data-testid="back-btn" title="Ir al inicio"><ArrowLeft className="w-5 h-5" /></Button>
            <div className="flex-1"><h1 className="text-2xl font-bold font-['Outfit']">Panel Docente</h1><p className="text-white/70 text-sm">Monitorea el progreso y obtiene sugerencias de aprendizaje</p></div>
            {/* Ver Tutores button */}
            <Button
              variant="ghost"
              className="text-white/90 hover:text-white hover:bg-white/10 border border-white/20 gap-2 hidden sm:flex"
              onClick={() => navigate("/dashboard")}
              data-testid="teacher-view-tutors-btn"
              title="Ver materias y tutores como estudiante"
            >
              <BookOpen className="w-4 h-4" />
              Ver Tutores
            </Button>
            {isIncidentResponsible && (
              <Button
                variant="ghost"
                className="text-white/90 hover:text-white hover:bg-white/10 border border-white/20 gap-2"
                onClick={() => navigate("/incidencias-sensibles")}
                data-testid="sensitive-incidents-btn"
                title="Bandeja privada de incidencias sensibles"
              >
                <ShieldAlert className="w-4 h-4" />
                <span className="hidden md:inline">Incidencias sensibles</span>
              </Button>
            )}
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger className="w-[140px] bg-white/10 border-white/20 text-white" data-testid="grade-filter"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Grado" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="4">4 Grado</SelectItem><SelectItem value="5">5 Grado</SelectItem><SelectItem value="6">6 Grado</SelectItem><SelectItem value="7">7 Grado</SelectItem><SelectItem value="8">8 Grado</SelectItem><SelectItem value="9">9 Grado</SelectItem><SelectItem value="1-bach">1 Bach</SelectItem><SelectItem value="2-bach">2 Bach</SelectItem></SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10" onClick={handleLogout} data-testid="logout-btn" title="Cerrar sesión">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 w-full">
        {/* KPIs */}
        {user?.role === "teacher" && stats?.teacherMetrics ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Asignados", value: stats.teacherMetrics.students_assigned, icon: Users, color: "blue" },
              { label: "Evidencias (total)", value: stats.teacherMetrics.evidences_total, icon: BookOpen, color: "purple" },
              { label: "Evidencias (7 dias)", value: stats.teacherMetrics.evidences_last_7d, icon: TrendingUp, color: "green" },
              { label: "Puntos (7 dias)", value: stats.teacherMetrics.points_last_7d, icon: Star, color: "yellow" },
            ].map((kpi, i) => (
              <Card key={kpi.label} className="border-gray-100">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-gray-600">{kpi.label}</CardTitle></CardHeader>
                <CardContent><div className="text-3xl font-bold text-gray-900">{kpi.value || 0}</div></CardContent>
              </Card>
            ))}
          </div>
        ) : null}

        {/* General Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[{ icon: Users, value: stats?.total_students || 0, label: "Estudiantes", color: "blue" },
            { icon: CheckCircle, value: stats?.sent_certifications || 0, label: "Enviados", color: "green" },
            { icon: Clock, value: stats?.pending_certifications || 0, label: "Pendientes", color: "yellow" },
            { icon: Award, value: stats?.total_certifications || 0, label: "Total Certs", color: "purple" }
          ].map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}><stat.icon className={`w-6 h-6 text-${stat.color}-500`} /></div>
                <div><p className="text-2xl font-bold text-gray-900">{stat.value}</p><p className="text-sm text-gray-500">{stat.label}</p></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <AnimatePresence mode="wait">
          {selectedStudent ? (
            <StudentDetail
              key="detail"
              student={selectedStudent}
              evidences={evidencesByStudent[selectedStudent.id || selectedStudent.user_id] || []}
              progressData={progressByStudent[selectedStudent.id || selectedStudent.user_id]}
              onBack={() => setSelectedStudent(null)}
            />
          ) : (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Tabs defaultValue="students" className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="students" data-testid="tab-students"><Users className="w-4 h-4 mr-2" />Estudiantes</TabsTrigger>
                  <TabsTrigger value="group-suggestions" data-testid="tab-group-suggestions"><Lightbulb className="w-4 h-4 mr-2" />Sugerencias Grupales</TabsTrigger>
                  <TabsTrigger value="paste-events" data-testid="tab-paste-events"><ShieldAlert className="w-4 h-4 mr-2" />Patrón IA</TabsTrigger>
                  <TabsTrigger value="reto-avanzo" data-testid="tab-reto-avanzo"><Brain className="w-4 h-4 mr-2" />Reto Avanzo</TabsTrigger>
                  <TabsTrigger value="certifications" data-testid="tab-certifications"><Award className="w-4 h-4 mr-2" />Certificaciones</TabsTrigger>
                  <TabsTrigger value="reflections" data-testid="tab-reflections"><MessageSquare className="w-4 h-4 mr-2" />Reflexiones</TabsTrigger>
                  <TabsTrigger value="export-csv" data-testid="tab-export-csv"><Download className="w-4 h-4 mr-2" />Exportar</TabsTrigger>
                </TabsList>
                
                <TabsContent value="students">
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    {/* Section pills filter — shown only when at least one section exists */}
                    {availableSections.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 px-5 pt-4" data-testid="section-filter-row">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Sección:</span>
                        <button
                          onClick={() => setSectionFilter("all")}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                            sectionFilter === "all"
                              ? "bg-[#419cca] text-white shadow-sm"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          data-testid="section-filter-all"
                        >
                          Todas <span className="opacity-70 ml-1">({students.length})</span>
                        </button>
                        {availableSections.map((sec) => {
                          const count = students.filter(
                            (s) => String(s.section || "").toUpperCase() === sec
                          ).length;
                          const gradeHint = gradeFilter === "all"
                            ? (students.find(
                                (s) => String(s.section || "").toUpperCase() === sec
                              )?.grade || "")
                            : gradeFilter;
                          return (
                            <button
                              key={sec}
                              onClick={() => setSectionFilter(sec)}
                              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                                sectionFilter === sec
                                  ? "bg-[#419cca] text-white shadow-sm"
                                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              }`}
                              data-testid={`section-filter-${sec}`}
                              title={`Grado ${gradeHint} sección ${sec}`}
                            >
                              {gradeHint}{sec} <span className="opacity-70 ml-1">({count})</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {filteredStudents.length === 0 ? (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">
                          {students.length === 0
                            ? "No hay estudiantes asignados"
                            : "Ningún estudiante en esta sección"}
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nombre</TableHead>
                            <TableHead>Grado</TableHead>
                            <TableHead>Sección</TableHead>
                            <TableHead>Evidencias</TableHead>
                            <TableHead>Última Evidencia</TableHead>
                            <TableHead>Promedio Unidad</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredStudents.map((s) => {
                            const ev = evidencesByStudent?.[s.id || s.user_id] || [];
                            const last = ev[0];
                            const progress = progressByStudent?.[s.id || s.user_id];
                            const unitMatch = progress?.units?.find(
                              (unit) => unit.unit === last?.unit && unit.subject === last?.subject
                            );
                            const unitAverage = Number.isFinite(unitMatch?.unit_average_percent)
                              ? unitMatch.unit_average_percent
                              : null;
                            const overallAverage = Number.isFinite(progress?.overall_average_percent)
                              ? progress.overall_average_percent
                              : null;
                            const displayAverage = unitAverage !== null ? unitAverage : overallAverage;
                            return (
                              <TableRow key={s.id || s.user_id} className="cursor-pointer hover:bg-gray-50/50" data-testid={`student-row-${s.id || s.user_id}`}>
                                <TableCell className="font-medium">{s.student_name || s.name}</TableCell>
                                <TableCell><Badge variant="outline">{s.grade || "Sin asignar"}</Badge></TableCell>
                                <TableCell>
                                  {s.section
                                    ? <Badge className="bg-indigo-50 text-indigo-700" data-testid={`student-section-${s.id || s.user_id}`}>{s.section}</Badge>
                                    : <span className="text-xs text-gray-400">—</span>}
                                </TableCell>
                                <TableCell>
                                  <Badge className={ev.length > 0 ? "bg-blue-50 text-blue-700" : "bg-gray-50 text-gray-600"}>
                                    {ev.length}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {last ? (
                                    <div className="text-sm">
                                      <div className="text-gray-900">{last.topic}</div>
                                      <div className="text-xs text-gray-400">{formatDate(last.created_at)}</div>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {displayAverage !== null ? (
                                    <Badge className="bg-blue-50 text-blue-700" data-testid={`student-unit-average-${s.id || s.user_id}`}>
                                      {displayAverage}%
                                    </Badge>
                                  ) : (
                                    <span className="text-sm text-gray-400" data-testid={`student-unit-average-${s.id || s.user_id}`}>
                                      -
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedStudent(s)}
                                    className="gap-1 text-[#419cca] hover:text-[#3584ad]"
                                    data-testid={`view-student-${s.id || s.user_id}`}
                                  >
                                    Ver detalle <ChevronRight className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="group-suggestions">
                  <GroupSuggestionsPanel students={students} />
                </TabsContent>

                <TabsContent value="paste-events">
                  <PasteEventsPanel />
                </TabsContent>

                <TabsContent value="reto-avanzo">
                  <RetoAvanzoPanel />
                </TabsContent>

                <TabsContent value="certifications">
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    {certifications.length === 0 ? (
                      <div className="text-center py-12"><Award className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No hay certificaciones</p></div>
                    ) : (
                      <Table><TableHeader><TableRow><TableHead>Estudiante</TableHead><TableHead>Tema</TableHead><TableHead>Grado</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead></TableRow></TableHeader>
                        <TableBody>{certifications.map((c) => <TableRow key={c.id}><TableCell className="font-medium">{c.student_name}</TableCell><TableCell>{c.topic}</TableCell><TableCell>{c.grade}</TableCell><TableCell><Badge className={c.status === "sent" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}>{c.status === "sent" ? "Enviado" : "Pendiente"}</Badge></TableCell><TableCell>{formatDate(c.created_at)}</TableCell></TableRow>)}</TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="reflections">
                  <div className="space-y-4">
                    {reflections.length === 0 ? (
                      <div className="bg-white rounded-2xl border border-gray-100 text-center py-12"><MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No hay reflexiones</p></div>
                    ) : (
                      reflections.map((r, i) => (
                        <motion.div key={r.id || `${r.student_name || ""}-${r.created_at || ""}-${i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl p-6 border border-gray-100">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10 flex items-center justify-center shrink-0"><GraduationCap className="w-6 h-6 text-[#419cca]" /></div>
                            <div className="flex-1"><div className="flex items-center gap-2 mb-2"><span className="font-semibold text-gray-900">{r.student_name}</span><Badge variant="outline">{r.grade}</Badge></div><p className="text-sm text-[#419cca] mb-3">{r.topic}</p><p className="text-gray-500 italic">&ldquo;{r.reflection}&rdquo;</p><p className="text-xs text-gray-400 mt-3">{formatDate(r.created_at)}</p></div>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="export-csv">
                  <ExportCSVPanel students={students} />
                </TabsContent>
              </Tabs>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <QHubFooter />
    </div>
  );
};

export default TeacherDashboard;
